import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useWorkspace } from '../context/WorkspaceContext';
import { useProject } from '../context/ProjectContext';
import { toast } from 'react-toast';
import {
  buildWorkspaceDashboardPath,
  getProjectIdFromPath,
  getWorkspaceIdFromPath,
  isWorkspaceDashboardRoute,
} from '../utils/workspaceRouteUtils';

const isUserAuthenticated = () => localStorage.getItem('isAuthenticated') === 'true';

/**
 * Custom hook for workspace-based routing and API management
 * Handles workspace extraction from URL, validation, and navigation
 */
export const useWorkspaceRouting = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentWorkspace, getWorkspaceFromURL } = useWorkspace();
  const { selectedProject } = useProject();

  /**
   * Get workspace ID from current URL
   */
  const getWorkspaceIdFromURL = useCallback(() => {
    return getWorkspaceIdFromPath(location.pathname);
  }, [location.pathname]);

  /**
   * Get project ID from current URL
   */
  const getProjectIdFromURL = useCallback(() => {
    return getProjectIdFromPath(location.pathname);
  }, [location.pathname]);

  /**
   * Navigate to workspace-specific route
   */
  const navigateToWorkspaceRoute = useCallback((path = '', workspace = currentWorkspace) => {
    if (!isUserAuthenticated()) {
      return;
    }

    if (!workspace) {
      toast.error('No workspace selected. Please select a workspace first.');
      navigate('/dashboard/all-workspaces');
      return;
    }

    if (!workspace?.id) {
      toast.error('Invalid workspace data');
      return;
    }

    const projectId = getProjectIdFromPath(location.pathname) || selectedProject?.id;
    const fullPath = buildWorkspaceDashboardPath(workspace.id, projectId, path);
    navigate(fullPath);
  }, [currentWorkspace, navigate, location.pathname, selectedProject?.id]);

  /**
   * Validate and synchronize workspace from URL
   * Returns workspace ID if valid, null otherwise
   */
  const syncWorkspaceFromURL = useCallback((userWorkspaceList = []) => {
    if (!isUserAuthenticated()) {
      return null;
    }

    const urlWorkspace = getWorkspaceFromURL(location.pathname);
    
    if (!urlWorkspace) {
      // No workspace in URL
      if (currentWorkspace) {
        // Redirect to workspace-specific route
        navigateToWorkspaceRoute('');
      }
      return null;
    }

    // Validate workspace exists in user's list
    const isValid = userWorkspaceList.some(w => w.id === urlWorkspace.id);
    
    if (!isValid) {
      // Invalid workspace ID in URL
      toast.error('Invalid workspace. Please select a valid workspace.');
      navigate('/dashboard/all-workspaces');
      return null;
    }

    // Workspace is valid
    return urlWorkspace.id;
  }, [location.pathname, currentWorkspace, getWorkspaceFromURL, navigateToWorkspaceRoute, navigate]);

  /**
   * Check if current route is workspace-based
   */
  const isWorkspaceRoute = useCallback(() => {
    return isWorkspaceDashboardRoute(location.pathname);
  }, [location.pathname]);

  /**
   * Get clean path without workspace prefix
   * E.g., "/workspace_1/project_2/dashboard/projects" -> "projects"
   */
  const getCleanPath = useCallback(() => {
    const match = location.pathname.match(/^\/[^/]+_\d+(?:\/project_\d+)?\/dashboard\/(.*)$/);
    return match ? match[1] : '';
  }, [location.pathname]);

  return {
    getWorkspaceIdFromURL,
    getProjectIdFromURL,
    navigateToWorkspaceRoute,
    syncWorkspaceFromURL,
    isWorkspaceRoute,
    getCleanPath,
    currentWorkspace,
  };
};

export default useWorkspaceRouting;
